import { useMemo, useState, useEffect } from 'react';
import {
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { safe } from '@/lib/calculations';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { detectAnomalies, max, riskMeta } from '@/lib/calculations';
import { formatNumber } from '@/lib/formatters';
import type { TelemetryRow } from '@/types';
import { DEFAULT_THRESHOLDS } from '@/config/constants';
import { CUSTOM_VAR_BY_KEY, ALERTS_UPDATED_EVENT } from '@/lib/alertDefs';
import { Activity, Waves, Zap, Gauge, Radio, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useLanguage } from '@/hooks/useLanguage';
import { SegmentedToggle } from '@/components/ui/SegmentedToggle';

interface AnomalySectionProps { meterRows: TelemetryRow[] }

/* -------------------------------------------------------------------------- */
/*  Storage sync                                                               */
/* -------------------------------------------------------------------------- */

const ALERT_STORAGE_KEY = 'cortex.alert.rules.v4';

interface StoredLevel { enabled: boolean; threshold: number }
interface StoredRule  { kind: string; warning: StoredLevel; critical: StoredLevel }

function loadThresholds(): Record<string, { warn: number; crit: number }> {
  try {
    const raw = localStorage.getItem(ALERT_STORAGE_KEY);
    if (!raw) return {};
    const rules = JSON.parse(raw) as StoredRule[];
    const out: Record<string, { warn: number; crit: number }> = {};
    for (const r of rules) {
      out[r.kind] = { warn: r.warning.threshold, crit: r.critical.threshold };
    }
    return out;
  } catch { return {}; }
}

/* -------------------------------------------------------------------------- */
/*  Custom alert field map                                                     */
/* -------------------------------------------------------------------------- */

interface StoredRuleFull extends StoredRule {
  id: string; label: string; enabled: boolean; isCustom?: boolean;
}

const CUSTOM_FIELD_GETTERS: Record<string, (r: TelemetryRow) => number> = {
  kwTotal:             r => safe(r.kwTotal),
  kvaTotal:            r => safe(r.kvaTotal),
  wattsTotal:          r => safe(r.wattsTotal ?? 0),
  currentTotal:        r => safe(r.currentTotal),
  currentR:            r => safe(r.currentR),
  currentY:            r => safe(r.currentY),
  currentB:            r => safe(r.currentB),
  energyKwh:           r => safe(r.energyKwh),
  dailyCostRs:         r => safe((r as TelemetryRow & { dailyCostRs?: number }).dailyCostRs ?? 0),
  carbonKgCo2:         r => safe((r as TelemetryRow & { carbonKgCo2?: number }).carbonKgCo2 ?? 0),
  wattsR:              r => safe((r as TelemetryRow & { wattsR?: number }).wattsR ?? 0),
  wattsY:              r => safe((r as TelemetryRow & { wattsY?: number }).wattsY ?? 0),
  wattsB:              r => safe((r as TelemetryRow & { wattsB?: number }).wattsB ?? 0),
  piTempC:             r => safe(r.piTempC),
  piCpuPct:            r => safe(r.piCpuPct),
  piMemPct:            r => safe(r.piMemPct),
  kFactorVMax:         r => Math.max(safe(r.kFactorVR), safe(r.kFactorVY), safe(r.kFactorVB)),
  kFactorIMax:         r => Math.max(safe(r.kFactorIR), safe(r.kFactorIY), safe(r.kFactorIB)),
  neutralCurrentA:     r => safe(r.neutralCurrentA),
  currentUnbalancePct: r => safe(r.currentUnbalancePct),
  voltageUnbalancePct: r => safe(r.voltageUnbalancePct ?? 0),
  loadPct:             r => safe(r.loadPct ?? 0),
  pfAvg:               r => Math.abs(safe(r.pfAvg)),
};

interface CustomMetricInfo {
  kind:  string;
  label: string;
  warn:  number;
  crit:  number;
}

function loadCustomMetrics(): CustomMetricInfo[] {
  try {
    const raw = localStorage.getItem(ALERT_STORAGE_KEY);
    if (!raw) return [];
    const rules = JSON.parse(raw) as StoredRuleFull[];
    return rules
      .filter(r => r.isCustom && r.enabled)
      .map(r => ({
        kind:  r.kind,
        label: r.label,
        warn:  r.warning.threshold,
        crit:  r.critical.threshold,
      }));
  } catch { return []; }
}

/* -------------------------------------------------------------------------- */
/*  Window toggle                                                              */
/* -------------------------------------------------------------------------- */

type Window = 'today' | 'week' | 'month' | 'lastMonth';

const WIN_OPTS: { value: Window; label: string }[] = [
  { value: 'today',     label: 'Today' },
  { value: 'week',      label: 'This Week' },
  { value: 'month',     label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
];

function sliceWindow(rows: TelemetryRow[], w: Window): TelemetryRow[] {
  if (!rows.length) return [];
  const now = new Date();
  const y = now.getFullYear(), mo = now.getMonth(), d = now.getDate();
  if (w === 'lastMonth') {
    // Last calendar month: 1st of last month 00:00:00 → last day of last month 23:59:59
    const from = new Date(y, mo - 1, 1, 0, 0, 0, 0).getTime();
    const to   = new Date(y, mo, 0, 23, 59, 59, 999).getTime();
    return rows.filter(r => { const t = r.timestamp.getTime(); return t >= from && t <= to; });
  }
  const from =
    w === 'today' ? new Date(y, mo, d, 0, 0, 0, 0).getTime()
    // This week: Sunday 00:00:00 of the current week
    : w === 'week'  ? new Date(y, mo, d - now.getDay(), 0, 0, 0, 0).getTime()
    : new Date(y, mo, 1, 0, 0, 0, 0).getTime();
  return rows.filter(r => r.timestamp.getTime() >= from);
}

function winLabel(w: Window): string {
  return w === 'today' ? 'today'
    : w === 'week'      ? 'this week'
    : w === 'lastMonth' ? 'last month'
    : 'this month';
}

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

type AlertSeverity = ReturnType<typeof detectAnomalies>[number]['severity'];

interface AlertInstance {
  ts:       Date;
  severity: AlertSeverity;
  value:    number;
  unit:     string;
}

interface MetricConfig {
  kind:           string;
  title:          string;
  description:    string;
  icon:           React.ReactNode;
  unit:           string;
  higherIsBad:    boolean;
  defaultWarn:    number;
  defaultCrit:    number;
  getValue:       (r: TelemetryRow) => number;
  getWorst:       (rows: TelemetryRow[]) => number;
  makeSeverityOf: (warn: number, crit: number) => (v: number) => AlertSeverity;
  /**
   * Optional data-availability check. When provided, the card is hidden if
   * this returns false for the current meter's rows — used to suppress the
   * THD card on meters that don't have harmonic registers (LK4405).
   */
  requiresData?:  (rows: TelemetryRow[]) => boolean;
}

/* -------------------------------------------------------------------------- */
/*  Metric definitions                                                         */
/* -------------------------------------------------------------------------- */

const METRICS: MetricConfig[] = [
  {
    kind: 'neutralCurrent',
    title: 'Neutral Current',
    description: 'High neutral current — possible fire risk from unbalanced phases or loose neutral.',
    icon: <Zap className="w-4 h-4" />,
    unit: 'A',
    higherIsBad: true,
    defaultWarn: DEFAULT_THRESHOLDS.neutralCurrentA,
    defaultCrit: DEFAULT_THRESHOLDS.neutralCurrentA * 3,
    getValue:  r => r.neutralCurrentA,
    getWorst:  rows => max(rows.map(r => r.neutralCurrentA)),
    makeSeverityOf: (warn, crit) => v =>
      v > crit              ? 'CRITICAL'
      : v > (warn + crit) / 2 ? 'HIGH'
      : v > warn              ? 'WARNING'
      : 'NORMAL',
  },
  {
    kind: 'overload',
    title: 'Approaching Contracted Demand',
    description: 'Load percentage nearing or exceeding contracted demand limit.',
    icon: <Gauge className="w-4 h-4" />,
    unit: '%',
    higherIsBad: true,
    defaultWarn: DEFAULT_THRESHOLDS.loadPctMax,
    defaultCrit: 100,
    getValue:  r => r.loadPct ?? 0,
    getWorst:  rows => max(rows.map(r => r.loadPct ?? 0)),
    makeSeverityOf: (warn, crit) => v =>
      v >= crit ? 'CRITICAL' : v >= warn ? 'WARNING' : 'NORMAL',
  },
  {
    kind: 'currentUnbalance',
    title: 'Current Imbalance',
    description: 'Phase current imbalance — indicates uneven load distribution across phases.',
    icon: <Activity className="w-4 h-4" />,
    unit: '%',
    higherIsBad: true,
    defaultWarn: DEFAULT_THRESHOLDS.currentUnbalancePct,
    defaultCrit: DEFAULT_THRESHOLDS.currentUnbalancePct * 2,
    getValue:  r => r.currentUnbalancePct,
    getWorst:  rows => max(rows.map(r => r.currentUnbalancePct)),
    makeSeverityOf: (warn, crit) => v =>
      v >= crit ? 'HIGH' : v >= warn ? 'WARNING' : 'NORMAL',
  },
  {
    kind: 'lowPF',
    title: 'Low Power Factor',
    description: 'Power factor below threshold — attracting TGSPDCL RKVAH penalty on energy bill.',
    icon: <Radio className="w-4 h-4" />,
    unit: 'PF',   // sentinel: fmtVal renders "PF 0.xxx" when unit === 'PF'
    higherIsBad: false,
    defaultWarn: DEFAULT_THRESHOLDS.pfMin,
    defaultCrit: 0.85,
    getValue:  r => Math.abs(r.pfAvg ?? 0),
    getWorst:  rows => {
      const vals = rows.map(r => Math.abs(r.pfAvg ?? 0)).filter(x => x > 0);
      return vals.length ? Math.min(...vals) : 0;
    },
    makeSeverityOf: (warn, crit) => v =>
      v > 0 && v <= crit ? 'HIGH'
      : v > 0 && v < warn ? 'WARNING'
      : 'NORMAL',
  },
  {
    kind: 'voltageUnbalance',
    title: 'Voltage Imbalance',
    description: 'Phase voltage imbalance — can damage motors and reduce equipment life.',
    icon: <Waves className="w-4 h-4" />,
    unit: '%',
    higherIsBad: true,
    defaultWarn: DEFAULT_THRESHOLDS.voltageUnbalancePct,
    defaultCrit: DEFAULT_THRESHOLDS.voltageUnbalancePct * 2,
    getValue:  r => r.voltageUnbalancePct ?? 0,
    getWorst:  rows => max(rows.map(r => r.voltageUnbalancePct ?? 0)),
    makeSeverityOf: (warn, crit) => v =>
      v >= crit ? 'HIGH' : v >= warn ? 'WARNING' : 'NORMAL',
  },
  {
    kind: 'highVoltageTHD',
    title: 'High Voltage Harmonic Distortion',
    description: 'Elevated voltage THD — can damage capacitors, transformers, and sensitive equipment. IEEE 519 limit is 8%.',
    icon: <Waves className="w-4 h-4" />,
    unit: '%',
    higherIsBad: true,
    defaultWarn: 5,
    defaultCrit: 8,
    getValue:  r => Math.max(safe(r.vRThdPct), safe(r.vYThdPct), safe(r.vBThdPct)),
    getWorst:  rows => {
      if (!rows.length) return 0;
      return Math.max(...rows.map(r => Math.max(safe(r.vRThdPct), safe(r.vYThdPct), safe(r.vBThdPct))));
    },
    makeSeverityOf: (warn, crit) => v =>
      v > crit ? 'HIGH' : v > warn ? 'WARNING' : 'NORMAL',
    requiresData: rows => rows.some(r =>
      safe(r.vRThdPct) > 0 || safe(r.vYThdPct) > 0 || safe(r.vBThdPct) > 0
    ),
  },
  {
    kind: 'highCurrentTHD',
    title: 'High Current Harmonic Distortion',
    description: 'Elevated current THD — indicates non-linear loads (VFDs, UPS, drives) causing transformer stress and overheating.',
    icon: <Waves className="w-4 h-4" />,
    unit: '%',
    higherIsBad: true,
    defaultWarn: 10,
    defaultCrit: 20,
    getValue:  r => Math.max(safe(r.iRThdPct), safe(r.iYThdPct), safe(r.iBThdPct)),
    getWorst:  rows => {
      if (!rows.length) return 0;
      return Math.max(...rows.map(r => Math.max(safe(r.iRThdPct), safe(r.iYThdPct), safe(r.iBThdPct))));
    },
    makeSeverityOf: (warn, crit) => v =>
      v > crit ? 'HIGH' : v > warn ? 'WARNING' : 'NORMAL',
    requiresData: rows => rows.some(r =>
      safe(r.iRThdPct) > 0 || safe(r.iYThdPct) > 0 || safe(r.iBThdPct) > 0
    ),
  },
  {
    kind: 'highVoltageKFactor',
    title: 'High Voltage K-Factor',
    description: 'Voltage K-Factor exceeds transformer derating limit — K>4 requires a K-rated transformer.',
    icon: <Zap className="w-4 h-4" />,
    unit: '',
    higherIsBad: true,
    defaultWarn: 4,
    defaultCrit: 13,
    getValue:  r => Math.max(safe(r.kFactorVR), safe(r.kFactorVY), safe(r.kFactorVB)),
    getWorst:  rows => {
      if (!rows.length) return 0;
      return Math.max(...rows.map(r => Math.max(safe(r.kFactorVR), safe(r.kFactorVY), safe(r.kFactorVB))));
    },
    makeSeverityOf: (warn, crit) => v =>
      v > crit ? 'HIGH' : v > warn ? 'WARNING' : 'NORMAL',
    requiresData: rows => rows.some(r =>
      safe(r.kFactorVR) > 0 || safe(r.kFactorVY) > 0 || safe(r.kFactorVB) > 0
    ),
  },
  {
    kind: 'highCurrentKFactor',
    title: 'High Current K-Factor',
    description: 'Current K-Factor indicates severe harmonic loading — transformer must be derated or replaced with K-rated unit.',
    icon: <Zap className="w-4 h-4" />,
    unit: '',
    higherIsBad: true,
    defaultWarn: 4,
    defaultCrit: 13,
    getValue:  r => Math.max(safe(r.kFactorIR), safe(r.kFactorIY), safe(r.kFactorIB)),
    getWorst:  rows => {
      if (!rows.length) return 0;
      return Math.max(...rows.map(r => Math.max(safe(r.kFactorIR), safe(r.kFactorIY), safe(r.kFactorIB))));
    },
    makeSeverityOf: (warn, crit) => v =>
      v > crit ? 'HIGH' : v > warn ? 'WARNING' : 'NORMAL',
    requiresData: rows => rows.some(r =>
      safe(r.kFactorIR) > 0 || safe(r.kFactorIY) > 0 || safe(r.kFactorIB) > 0
    ),
  },
];

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function fmtDT(ts: Date): string {
  return ts.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

/**
 * Format a metric value with its unit for display.
 * unit === 'PF' is the sentinel used only by the lowPF MetricConfig to
 * produce "PF 0.xxx" display — it is never shown as a literal unit suffix.
 */
function fmtVal(value: number, unit: string): string {
  if (unit === 'PF') return `PF ${formatNumber(value, 3)}`;
  if (!unit)         return formatNumber(value, 3);
  return `${formatNumber(value)}${unit}`;
}

function topSev(instances: AlertInstance[]): AlertSeverity {
  if (instances.some(i => i.severity === 'CRITICAL')) return 'CRITICAL';
  if (instances.some(i => i.severity === 'HIGH'))     return 'HIGH';
  if (instances.some(i => i.severity === 'WARNING'))  return 'WARNING';
  return instances[0]?.severity ?? ('NORMAL' as AlertSeverity);
}

/* -------------------------------------------------------------------------- */
/*  Zone split computation                                                     */
/* -------------------------------------------------------------------------- */

interface ZoneSplit {
  safePct:     number;
  warningPct:  number;
  criticalPct: number;
}

/**
 * For each row in `rows`, classify it as SAFE / WARNING / CRITICAL using
 * `severityOf`. Returns percentage of total rows in each zone.
 *
 * Note: we collapse CRITICAL + HIGH → "Critical zone" and WARNING → "Warning"
 * so the three-zone model stays intuitive.
 */
function computeZoneSplit(
  rows:       TelemetryRow[],
  config:     MetricConfig,
  severityOf: (v: number) => AlertSeverity,
): ZoneSplit {
  if (!rows.length) return { safePct: 100, warningPct: 0, criticalPct: 0 };
  let safe = 0, warning = 0, critical = 0;
  for (const r of rows) {
    const sev = severityOf(config.getValue(r));
    if (sev === 'CRITICAL' || sev === 'HIGH') critical++;
    else if (sev === 'WARNING')               warning++;
    else                                      safe++;
  }
  const total = rows.length;
  return {
    safePct:     (safe     / total) * 100,
    warningPct:  (warning  / total) * 100,
    criticalPct: (critical / total) * 100,
  };
}

/* -------------------------------------------------------------------------- */
/*  Zone pie chart constants                                                   */
/* -------------------------------------------------------------------------- */

const ZONE_COLORS = {
  safe:     '#10B981',  // emerald-500
  warning:  '#F59E0B',  // amber-400
  critical: '#EF4444',  // red-500
};

const ZONE_EMPTY_COLOR = '#E2E8F0'; // slate-200 — shown when no data

/* -------------------------------------------------------------------------- */
/*  MetricZonePie — donut showing Safe / Warning / Critical % of time         */
/* -------------------------------------------------------------------------- */

interface MetricZonePieProps {
  split:       ZoneSplit;
  win:         Window;
  warnThr:     number;
  critThr:     number;
  unit:        string;
  higherIsBad: boolean;
}

function MetricZonePie({ split, win, warnThr, critThr, unit, higherIsBad }: MetricZonePieProps) {
  const { safePct, warningPct, criticalPct } = split;
  const { t } = useLanguage();

  const hasData = safePct + warningPct + criticalPct > 0;

  const rawData = [
    { name: t('Safe'),     value: safePct,     color: ZONE_COLORS.safe     },
    { name: t('Warning'),  value: warningPct,  color: ZONE_COLORS.warning  },
    { name: t('Critical'), value: criticalPct, color: ZONE_COLORS.critical },
  ].filter(d => d.value > 0);

  const chartData = rawData.length
    ? rawData
    : [{ name: t('No data'), value: 1, color: ZONE_EMPTY_COLOR }];

  // Dominant zone for centre label
  const dominant =
    criticalPct >= warningPct && criticalPct >= safePct ? 'critical'
    : warningPct >= safePct                             ? 'warning'
    : 'safe';

  const centreColor =
    dominant === 'critical' ? ZONE_COLORS.critical
    : dominant === 'warning' ? ZONE_COLORS.warning
    : ZONE_COLORS.safe;

  const centrePct =
    dominant === 'critical' ? criticalPct
    : dominant === 'warning' ? warningPct
    : safePct;

  const centreLabel =
    dominant === 'critical' ? t('CRIT')
    : dominant === 'warning' ? t('WARN')
    : t('SAFE');

  return (
    <div className="flex items-center gap-4 py-3">
      {/* Donut */}
      <div className="w-28 h-28 shrink-0 relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%" cy="50%"
              innerRadius={32} outerRadius={50}
              paddingAngle={rawData.length > 1 ? 3 : 0}
              minAngle={rawData.length ? 4 : 0}
              dataKey="value"
              stroke="#ffffff"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {chartData.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {/* Centre label */}
        {hasData && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="num text-[13px] font-extrabold leading-none" style={{ color: centreColor }}>
              {centrePct.toFixed(0)}%
            </span>
            <span className="text-[9px] font-bold tracking-wide mt-0.5" style={{ color: centreColor }}>
              {centreLabel}
            </span>
          </div>
        )}
      </div>

      {/* Legend + bars — show actual threshold values */}
      <div className="flex-1 space-y-2">
        {[
          {
            label: t('Safe'),
            pct:   safePct,
            color: ZONE_COLORS.safe,
            desc:  higherIsBad ? `${t('Below')} ${warnThr}${unit === 'PF' ? '' : unit}` : `${t('Above')} ${warnThr}${unit === 'PF' ? '' : unit}`,
          },
          {
            label: unit === 'PF' ? `${t('Warning')} (PF ${warnThr.toFixed(2)} – ${critThr.toFixed(2)})`
              : `${t('Warning')} (${warnThr}${unit} – ${critThr}${unit})`,
            pct:   warningPct,
            color: ZONE_COLORS.warning,
            desc:  higherIsBad
              ? `${t('Between')} ${warnThr}${unit === 'PF' ? '' : unit} ${t('and')} ${critThr}${unit === 'PF' ? '' : unit}`
              : `${t('Between')} ${critThr}${unit === 'PF' ? '' : unit} ${t('and')} ${warnThr}${unit === 'PF' ? '' : unit}`,
          },
          {
            label: unit === 'PF' ? `${t('Critical')} (PF < ${critThr.toFixed(2)})`
              : `${t('Critical')} (> ${critThr}${unit})`,
            pct:   criticalPct,
            color: ZONE_COLORS.critical,
            desc:  higherIsBad ? `${t('Above')} ${critThr}${unit === 'PF' ? '' : unit}` : `${t('Below')} ${critThr}${unit === 'PF' ? '' : unit}`,
          },
        ].map(({ label, pct, color, desc }) => (
          <div key={label}>
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
                <span className="text-[10px] font-semibold text-slate-700">{label}</span>
              </div>
              <span className="num text-[10px] font-bold text-slate-900">
                {pct.toFixed(1)}%
              </span>
            </div>
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, background: color }}
              />
            </div>
            <p className="text-[9px] text-slate-400 mt-0.5">{desc}</p>
          </div>
        ))}
        <p className="text-[9px] text-slate-400 pt-0.5">{t("% of readings")} · {t(winLabel(win))}</p>
      </div>
    </div>
  );
}

/* SegmentedToggle is now used here instead of the custom WinToggle */

/* -------------------------------------------------------------------------- */
/*  MetricCard                                                                 */
/* -------------------------------------------------------------------------- */

interface MetricCardProps {
  config:     MetricConfig;
  latest:     TelemetryRow | undefined;
  allRows:    TelemetryRow[];
  winRows:    TelemetryRow[];
  win:        Window;
  thresholds: Record<string, { warn: number; crit: number }>;
}

function MetricCard({ config, latest, allRows, winRows, win, thresholds }: MetricCardProps) {
  const [open,     setOpen]     = useState(false);
  const [chartWin, setChartWin] = useState<Window>(win);
  const { t } = useLanguage();

  const stored  = thresholds[config.kind];
  const warnThr = stored?.warn ?? config.defaultWarn;
  const critThr = stored?.crit ?? config.defaultCrit;

  const severityOf = useMemo(
    () => config.makeSeverityOf(warnThr, critThr),
    [config, warnThr, critThr],
  );

  const chartRows = useMemo(
    () => sliceWindow(allRows, chartWin),
    [allRows, chartWin],
  );

  const zoneSplit = useMemo(
    () => computeZoneSplit(chartRows, config, severityOf),
    [chartRows, config, severityOf],
  );

  const instances = useMemo<AlertInstance[]>(() => {
    const out: AlertInstance[] = [];
    for (let i = allRows.length - 1; i >= 0 && out.length < 5; i--) {
      const r   = allRows[i];
      const val = config.getValue(r);
      const sev = severityOf(val);
      if (sev === 'NORMAL') continue;
      out.push({ ts: r.timestamp, severity: sev, value: val, unit: config.unit });
    }
    return out;
  }, [allRows, config, severityOf]);

  const liveVal      = latest != null ? config.getValue(latest) : null;
  const worstVal     = config.getWorst(winRows);
  const histSev      = topSev(instances);
  const badgeBg      = histSev === 'CRITICAL' || histSev === 'HIGH' ? 'bg-red-500'
                     : histSev === 'WARNING' ? 'bg-amber-400' : 'bg-emerald-500';
  const unitSuffix = config.unit === 'PF' ? '' : config.unit;
  const fmtThr = (v: number) => config.unit === 'PF' ? v.toFixed(2) : String(v);
  const warnLabel = config.higherIsBad
    ? `${t("Warning above")} ${fmtThr(warnThr)}${unitSuffix}`
    : `${t("Warning below")} ${fmtThr(warnThr)}${unitSuffix}`;
  const critLabel = config.higherIsBad
    ? `${t("Critical above")} ${fmtThr(critThr)}${unitSuffix}`
    : `${t("Critical below")} ${fmtThr(critThr)}${unitSuffix}`;

  // Colour the anomaly density indicator using zone split
  const critPct = zoneSplit.criticalPct;
  const warnPct = zoneSplit.warningPct;
  const densityColor = critPct >= 10 ? 'text-red-600'
                     : warnPct >= 20 ? 'text-amber-600' : 'text-emerald-600';

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full px-4 py-4 flex items-start gap-3 text-left"
        aria-expanded={open}
      >
        <span className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5 bg-emerald-50 text-emerald-500">
          {config.icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-sm font-semibold text-slate-800">{t(config.title)}</span>
            {instances.length > 0 && (
              <span className={cn('inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold text-white', badgeBg)}>
                {instances.length}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-0.5">{t("Live")}</p>
              <p className="num text-lg font-bold leading-none text-slate-900">
                {liveVal != null ? fmtVal(liveVal, config.unit) : '—'}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-0.5">
                {t("Worst")} · {t(winLabel(win))}
              </p>
              <p className="num text-lg font-bold leading-none text-slate-900">
                {fmtVal(worstVal, config.unit)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-2">
            <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">⚠ {t(warnLabel)}</span>
            <span className="text-[10px] font-medium text-red-500 bg-red-50 px-1.5 py-0.5 rounded">● {t(critLabel)}</span>
          </div>
        </div>
        <ChevronDown className={cn('w-4 h-4 text-slate-500 shrink-0 mt-1 transition-transform duration-200', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="border-t border-slate-100">
          <p className="px-4 py-2.5 text-[11px] text-slate-700 leading-relaxed bg-slate-50 border-b border-slate-100">
            {t(config.description)}
          </p>
          <div className="px-4 pt-3 pb-1">
            <div className="flex items-center justify-between mb-1">
              <div>
                <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-0.5">{t("Time distribution")}</p>
                <p className={cn('num text-2xl font-extrabold', densityColor)}>
                  {(zoneSplit.criticalPct + zoneSplit.warningPct).toFixed(1)}<span className="text-base font-bold">%</span>
                </p>
                <p className="text-[10px] text-slate-500">{t("over-threshold")} · {t(winLabel(chartWin))}</p>
              </div>
              <SegmentedToggle size="sm" value={chartWin} onChange={setChartWin} options={WIN_OPTS} ariaLabel="Time distribution window" />
            </div>
            {chartRows.length > 0
              ? <MetricZonePie
                  split={zoneSplit}
                  win={chartWin}
                  warnThr={warnThr}
                  critThr={critThr}
                  unit={config.unit}
                  higherIsBad={config.higherIsBad}
                />
              : <p className="text-xs text-slate-500 py-3 text-center">{t("No data for")} {t(winLabel(chartWin))}.</p>}
          </div>
          <div className="border-t border-slate-100">
            {instances.length > 0 ? (
              <>
                <p className="px-4 pt-2.5 pb-1 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                  {t("Last 5 instances · all time")}
                </p>
                <ul className="divide-y divide-slate-100">
                  {instances.map((inst, idx) => (
                    <li key={idx} className="px-4 py-2.5 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] font-bold text-slate-500 tabular-nums shrink-0">#{idx + 1}</span>
                        <span className="text-xs font-semibold text-slate-800">{fmtDT(inst.ts)}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="num text-sm font-bold text-slate-800">{fmtVal(inst.value, inst.unit)}</span>
                        <StatusBadge level={inst.severity} />
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="px-4 py-2 text-[11px] font-semibold text-slate-500 border-t border-slate-100">
                  {t("Most recent 5 breach events · reflects your current thresholds")}
                </p>
              </>
            ) : (
              <p className="px-4 py-4 text-sm font-semibold text-slate-600 text-center">
                {t("No breach events with current thresholds")}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main export                                                                */
/* -------------------------------------------------------------------------- */

export function AnomalySection({ meterRows }: AnomalySectionProps) {
  const { t } = useLanguage();
  const [win, setWin] = useState<Window>('today');

  const [thresholds, setThresholds] = useState<Record<string, { warn: number; crit: number }>>(
    () => loadThresholds(),
  );

  const [customMetrics, setCustomMetrics] = useState<CustomMetricInfo[]>(() => loadCustomMetrics());

  useEffect(() => {
    const reload = () => { setThresholds(loadThresholds()); setCustomMetrics(loadCustomMetrics()); };
    window.addEventListener('storage',            reload);  // cross-tab
    window.addEventListener('focus',              reload);  // tab re-focus
    window.addEventListener(ALERTS_UPDATED_EVENT, reload);  // same-tab real-time
    return () => {
      window.removeEventListener('storage',            reload);
      window.removeEventListener('focus',              reload);
      window.removeEventListener(ALERTS_UPDATED_EVENT, reload);
    };
  }, []);

  const latest      = meterRows[meterRows.length - 1];
  const latestHits  = latest ? detectAnomalies(latest) : [];
  const winRowsData = useMemo(() => sliceWindow(meterRows, win), [meterRows, win]);

  return (
    <section className="space-y-3">

      {latest && (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{t("Current status")}</p>
              <p className="text-sm font-semibold text-slate-800">
                {latestHits.length
                  ? `${latestHits.length} ${latestHits.length > 1 ? t('active issues') : t('active issue')}`
                  : t('All systems normal')}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {latestHits.length ? t('Detected on most recent reading') : t('No breaches on latest reading')}
              </p>
            </div>
            <StatusBadge level={latest.fireRiskLevel} pulse={latest.fireRiskLevel !== 'NORMAL'} />
          </div>
          {latestHits.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {latestHits.map(h => (
                <li key={h.kind} className="flex items-center justify-between rounded-xl px-3 py-2 bg-slate-50">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: riskMeta(h.severity).color }} />
                    <span className="text-xs font-medium text-slate-700">{t(h.label)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="num text-xs font-bold text-slate-800">
                      {h.kind === 'lowPF' ? h.value.toFixed(3) : formatNumber(h.value)}
                      <span className="font-medium text-slate-500 ml-0.5">
                        {h.kind === 'lowPF' ? '' : h.kind === 'overload' || h.kind.includes('Unbalance') ? '%' : 'A'}
                      </span>
                    </span>
                    <StatusBadge level={h.severity} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex items-center justify-between px-0.5">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
          {t("Metrics · last 5 alerts")}
        </p>
        <SegmentedToggle size="sm" value={win} onChange={setWin} options={WIN_OPTS} ariaLabel="Metrics window" />
      </div>

      {METRICS.filter(config => !config.requiresData || config.requiresData(meterRows)).map(config => (
        <MetricCard
          key={config.kind}
          config={config}
          latest={latest}
          allRows={meterRows}
          winRows={winRowsData}
          win={win}
          thresholds={thresholds}
        />
      ))}

      {/* Custom alerts from Alerts page */}
      {customMetrics.length > 0 && (
        <>
          <div className="flex items-center justify-between px-0.5 mt-1">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
              {t("Custom Alerts")} · {customMetrics.length} {t("active")}
            </p>
          </div>
          {customMetrics.map(cm => {
            const fieldKey  = cm.kind.replace('custom_', '');
            const getter    = CUSTOM_FIELD_GETTERS[fieldKey];
            if (!getter) return null;

            const varMeta   = CUSTOM_VAR_BY_KEY[fieldKey];
            const unit      = varMeta?.unit        ?? '';
            const higherIs  = varMeta?.higherIsBad ?? true;
            const desc      = varMeta?.description ?? `Custom alert — warn ${cm.warn}, critical ${cm.crit}`;

            const customConfig: MetricConfig = {
              kind:        cm.kind,
              title:       cm.label,
              description: `${t(desc)} · ${t("threshold")}: ${t("warn")} ${cm.warn}${unit}, ${t("critical")} ${cm.crit}${unit}`,
              icon:        <Activity className="w-4 h-4" />,
              unit,
              higherIsBad: higherIs,
              defaultWarn: cm.warn,
              defaultCrit: cm.crit,
              getValue:    getter,
              getWorst:    rows => {
                const vals = rows.map(getter).filter(v => Number.isFinite(v) && v > 0);
                if (!vals.length) return 0;
                return higherIs ? Math.max(...vals) : Math.min(...vals);
              },
              makeSeverityOf: (warn, crit) => v =>
                higherIs
                  ? (v >= crit ? 'CRITICAL' : v >= warn ? 'WARNING' : 'NORMAL')
                  : (v > 0 && v <= crit ? 'CRITICAL' : v > 0 && v < warn ? 'WARNING' : 'NORMAL'),
            };
            return (
              <MetricCard
                key={cm.kind}
                config={customConfig}
                latest={latest}
                allRows={meterRows}
                winRows={winRowsData}
                win={win}
                thresholds={thresholds}
              />
            );
          })}
        </>
      )}

    </section>
  );
}